import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AudioLines,
  Clapperboard,
  ExternalLink,
  FileJson,
  Image as ImageIcon,
  Music,
  Video,
} from "lucide-react";

import type { EchoflicksSessionMedia, EchoflicksVideoSession } from "../types";
import { EmptyHint } from "./EchoflicksSessionList";
import { isAnimatedScene } from "./scriptStory";
import { isImage, isVideo } from "./sessionUtils";
import { useDisplayableImage } from "./useDisplayableImage";
import { resolveMediaSource } from "./mediaSource";
import { sessionScriptSource, useMediaUrl } from "./useMediaUrl";
import { DisplayCropBadge } from "./DisplayCropBadge";

type ScriptEnvelope = {
  style?: unknown;
  generated?: unknown;
  bboxes?: unknown;
  diagnostics?: ScriptDiagnostics;
  script?: DirectorScript;
  [key: string]: unknown;
};

type ScriptDiagnostics = {
  title_card?: TitleCardDiagnostics;
  [key: string]: unknown;
};

type TitleCardDiagnostics = {
  guidance?: {
    preferred?: unknown;
    suitable?: unknown;
    rationale?: unknown;
    [key: string]: unknown;
  };
  selection?: {
    selected_card_types?: unknown;
    followed_recommendation?: unknown;
    matched_preferred?: unknown;
    non_recommended_selected?: unknown;
    unselected_preferred?: unknown;
    llm_decision_rationale?: unknown;
    mismatch_reason?: unknown;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type DirectorScript = {
  title?: string;
  description?: string;
  video_content?: ScriptScene[];
  audio_content?: AudioCue[];
  music_prompt?: MusicPrompt;
  voice_generation?: VoiceGeneration;
  aspect_ratio?: string;
  filter?: string | null;
  lut?: string | null;
  typography?: unknown;
  [key: string]: unknown;
};

type ScriptScene = {
  id?: number;
  type?: string;
  video_prompt?: ScenePrompt;
  transition?: string;
  [key: string]: unknown;
};

type ScenePrompt = {
  file?: string;
  length?: number;
  prompt?: string | null;
  generation_prompt?: string | null;
  static_effect?: string;
  cuts?: number[];
  card_type?: string;
  text?: string;
  main?: boolean;
  title_card_text?: string;
  title_card_bg?: string;
  [key: string]: unknown;
};

type AudioCue = {
  start_second?: number;
  voice_over_prompt?: string | null;
  [key: string]: unknown;
};

type MusicPrompt = {
  composition_plan?: {
    positive_global_styles?: string[];
    negative_global_styles?: string[];
    sections?: MusicSection[];
    [key: string]: unknown;
  };
  audio_ducking?: AudioDucking[];
  [key: string]: unknown;
};

type MusicSection = {
  section_name?: string;
  positive_local_styles?: string[];
  negative_local_styles?: string[];
  duration_ms?: number;
  lines?: unknown[];
  [key: string]: unknown;
};

type AudioDucking = {
  level?: string;
  start_second?: number;
  end_second?: number;
  [key: string]: unknown;
};

type VoiceGeneration = {
  gen_type?: string | null;
  voice_id?: string | null;
  prompt?: string | null;
  [key: string]: unknown;
};

export const ScriptJsonViewer = ({
  session,
  media,
}: {
  session: EchoflicksVideoSession;
  media: EchoflicksSessionMedia[];
}) => {
  const scriptMedia = media.find((item) => item.kind === "script_json");
  const scriptUrlState = useMediaUrl(
    resolveMediaSource(sessionScriptSource(session), scriptMedia),
  );
  const scriptUrl = scriptUrlState.url;
  const [state, setState] = useState<
    | { status: "idle" | "loading" }
    | { status: "loaded"; envelope: ScriptEnvelope }
    | { status: "error"; message: string }
  >({ status: scriptUrl ? "loading" : "idle" });

  useEffect(() => {
    if (!scriptUrl) {
      setState({ status: "idle" });
      return;
    }

    let active = true;
    setState({ status: "loading" });
    fetch(scriptUrl)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Script request failed with ${response.status}`);
        }
        return (await response.json()) as ScriptEnvelope;
      })
      .then((envelope) => {
        if (active) setState({ status: "loaded", envelope });
      })
      .catch((error) => {
        if (active) {
          setState({
            status: "error",
            message:
              error instanceof Error ? error.message : "Could not load script",
          });
        }
      });

    return () => {
      active = false;
    };
  }, [scriptUrl]);

  if (!scriptUrl && scriptUrlState.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Director Script</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            Preparing a private signed script URL...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!scriptUrl && scriptUrlState.status === "error") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Director Script</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyHint>{scriptUrlState.error}</EmptyHint>
        </CardContent>
      </Card>
    );
  }

  if (!scriptUrl) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Director Script</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyHint>No script JSON is linked to this session.</EmptyHint>
        </CardContent>
      </Card>
    );
  }

  if (state.status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Director Script</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            Loading script JSON...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (state.status === "error") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Director Script</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <EmptyHint>{state.message}</EmptyHint>
          <AssetLink href={scriptUrl} label="Open script JSON" />
        </CardContent>
      </Card>
    );
  }

  if (state.status !== "loaded") {
    return null;
  }

  return (
    <ScriptInspector
      envelope={state.envelope}
      media={media}
      scriptUrl={scriptUrl}
    />
  );
};

const ScriptInspector = ({
  envelope,
  media,
  scriptUrl,
}: {
  envelope: ScriptEnvelope;
  media: EchoflicksSessionMedia[];
  scriptUrl: string;
}) => {
  const script = envelope.script ?? (envelope as DirectorScript);
  const scenes = script.video_content ?? [];
  const audio = script.audio_content ?? [];
  const musicPrompt = script.music_prompt;
  const composition = musicPrompt?.composition_plan;
  const musicSections = composition?.sections ?? [];
  const mediaByFilename = useMemo(() => buildMediaMap(media), [media]);
  const totalDuration = sumSceneDuration(scenes);
  // Animation is prompt-aware, not type-based: a prompted StaticImageScene is
  // routed to the SECONDARY i2v provider, so it moves.
  const heroCount = scenes.filter(
    (scene) => scene.type === "ImageToVideoScene",
  ).length;
  const secondaryCount = scenes.filter(
    (scene) => scene.type === "StaticImageScene" && isAnimatedScene(scene),
  ).length;
  const i2vCount = heroCount + secondaryCount;
  const staticCount = scenes.filter(
    (scene) => scene.type === "StaticImageScene" && !isAnimatedScene(scene),
  ).length;
  const titleCount = scenes.filter(
    (scene) => scene.type === "TitleCardScene",
  ).length;
  const videoCount = scenes.filter(
    (scene) => scene.type === "VideoScene",
  ).length;
  const titleCardDiagnostics = getTitleCardDiagnostics(envelope.diagnostics);
  const tabsListClassName = titleCardDiagnostics
    ? "grid h-auto w-full grid-cols-6"
    : "grid h-auto w-full grid-cols-5";

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Director Script</CardTitle>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="secondary">
                {script.aspect_ratio ?? "Aspect unknown"}
              </Badge>
              <Badge variant="outline">{script.filter ?? "No filter"}</Badge>
              <Badge variant="outline">{script.lut ?? "No LUT"}</Badge>
              {typeof envelope.style === "string" ? (
                <Badge>{envelope.style}</Badge>
              ) : null}
            </div>
          </div>
          <AssetLink href={scriptUrl} label="Open JSON" />
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className={tabsListClassName}>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="scenes">Scenes</TabsTrigger>
            <TabsTrigger value="voice">Voice</TabsTrigger>
            <TabsTrigger value="music">Music</TabsTrigger>
            {titleCardDiagnostics ? (
              <TabsTrigger value="diagnostics">Diagnostics</TabsTrigger>
            ) : null}
            <TabsTrigger value="raw">Raw</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            <div>
              <h3 className="text-lg font-semibold">
                {script.title ?? "Untitled script"}
              </h3>
              {script.description ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  {script.description}
                </p>
              ) : null}
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Metric
                icon={<Clapperboard className="size-4" />}
                label="Scenes"
                value={scenes.length.toString()}
              />
              <Metric
                icon={<Video className="size-4" />}
                label="Runtime Plan"
                value={formatSeconds(totalDuration)}
              />
              <Metric
                icon={<AudioLines className="size-4" />}
                label="Voice Cues"
                value={audio.length.toString()}
              />
              <Metric
                icon={<Music className="size-4" />}
                label="Music Sections"
                value={musicSections.length.toString()}
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-md border p-4">
                <h4 className="font-medium">Scene Mix</h4>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <MiniStat label="Title cards" value={titleCount} />
                  <MiniStat label="Still images" value={staticCount} />
                  <MiniStat
                    label={
                      secondaryCount > 0
                        ? `In motion (${heroCount} primary + ${secondaryCount} secondary)`
                        : "In motion (i2v)"
                    }
                    value={i2vCount}
                  />
                  <MiniStat label="Video clips" value={videoCount} />
                </div>
              </div>
              <div className="rounded-md border p-4">
                <h4 className="font-medium">Generation Settings</h4>
                <dl className="mt-3 grid gap-2 text-sm">
                  <InlineMeta
                    label="Voice"
                    value={voiceLabel(script.voice_generation)}
                  />
                  <InlineMeta
                    label="Typography"
                    value={jsonSummary(script.typography)}
                  />
                  <InlineMeta
                    label="Generated"
                    value={jsonSummary(envelope.generated)}
                  />
                  <InlineMeta
                    label="BBoxes"
                    value={jsonSummary(envelope.bboxes)}
                  />
                </dl>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="scenes" className="mt-4">
            {scenes.length > 0 ? (
              <div className="space-y-3">
                {scenes.map((scene, index) => (
                  <SceneRow
                    key={`${scene.id ?? index}-${scene.type ?? "scene"}`}
                    scene={scene}
                    media={
                      scene.video_prompt?.file
                        ? mediaByFilename.get(scene.video_prompt.file)
                        : undefined
                    }
                  />
                ))}
              </div>
            ) : (
              <EmptyHint>No scene plan exists in this script.</EmptyHint>
            )}
          </TabsContent>

          <TabsContent value="voice" className="mt-4 space-y-4">
            <div className="rounded-md border p-4 text-sm">
              <h4 className="font-medium">Voice Generation</h4>
              <dl className="mt-3 grid gap-2">
                <InlineMeta
                  label="Type"
                  value={script.voice_generation?.gen_type ?? "None"}
                />
                <InlineMeta
                  label="Voice ID"
                  value={script.voice_generation?.voice_id ?? "None"}
                />
                <InlineMeta
                  label="Prompt"
                  value={script.voice_generation?.prompt ?? "None"}
                />
              </dl>
            </div>
            {audio.length > 0 ? (
              <div className="space-y-3">
                {audio.map((cue, index) => (
                  <div
                    key={`${cue.start_second ?? index}`}
                    className="rounded-md border p-4"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">
                        {formatTimestamp(cue.start_second)}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        Cue {index + 1}
                      </span>
                    </div>
                    {cue.voice_over_prompt ? (
                      <p className="mt-3 whitespace-pre-wrap text-sm">
                        {cue.voice_over_prompt}
                      </p>
                    ) : (
                      <p className="mt-3 text-sm text-muted-foreground">
                        No narration text.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyHint>No voiceover cues exist in this script.</EmptyHint>
            )}
          </TabsContent>

          <TabsContent value="music" className="mt-4 space-y-4">
            <StyleCloud
              title="Global Positive Styles"
              values={composition?.positive_global_styles}
            />
            <StyleCloud
              title="Global Negative Styles"
              values={composition?.negative_global_styles}
              muted
            />
            {musicSections.length > 0 ? (
              <div className="space-y-3">
                {musicSections.map((section, index) => (
                  <MusicSectionCard
                    key={`${section.section_name ?? index}`}
                    section={section}
                  />
                ))}
              </div>
            ) : (
              <EmptyHint>No music sections exist in this script.</EmptyHint>
            )}
            <AudioDuckingList ducking={musicPrompt?.audio_ducking ?? []} />
          </TabsContent>

          {titleCardDiagnostics ? (
            <TabsContent value="diagnostics" className="mt-4">
              <TitleCardDiagnosticsPanel diagnostics={titleCardDiagnostics} />
            </TabsContent>
          ) : null}

          <TabsContent value="raw" className="mt-4">
            <pre className="max-h-[560px] overflow-auto rounded-md bg-muted p-4 text-xs">
              {JSON.stringify(envelope, null, 2)}
            </pre>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

const TitleCardDiagnosticsPanel = ({
  diagnostics,
}: {
  diagnostics: TitleCardDiagnostics;
}) => {
  const guidance = isRecord(diagnostics.guidance) ? diagnostics.guidance : {};
  const selection = isRecord(diagnostics.selection)
    ? diagnostics.selection
    : {};
  const preferred = stringList(guidance.preferred);
  const suitable = stringList(guidance.suitable);
  const selected = stringList(selection.selected_card_types);
  const nonRecommended = stringList(selection.non_recommended_selected);
  const unselectedPreferred = stringList(selection.unselected_preferred);
  const followed = booleanValue(selection.followed_recommendation);
  const matchedPreferred = booleanValue(selection.matched_preferred);
  const llmRationale = stringValue(selection.llm_decision_rationale);
  const mismatchReason = stringValue(selection.mismatch_reason);
  const guidanceRationale = stringValue(guidance.rationale);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-md border p-4">
          <h4 className="font-medium">Recommendation</h4>
          <div className="mt-3 space-y-3 text-sm">
            <TitleCardBadgeList label="Preferred" values={preferred} />
            <TitleCardBadgeList label="Suitable" values={suitable} muted />
            <InlineMeta label="Reason" value={guidanceRationale} />
          </div>
        </div>

        <div className="rounded-md border p-4">
          <h4 className="font-medium">Felix Selection</h4>
          <div className="mt-3 space-y-3 text-sm">
            <TitleCardBadgeList label="Selected" values={selected} />
            <div className="flex flex-wrap gap-2">
              <DecisionBadge label="Followed recommendation" value={followed} />
              <DecisionBadge
                label="Matched preferred"
                value={matchedPreferred}
              />
            </div>
            <TitleCardBadgeList
              label="Outside recommendation"
              values={nonRecommended}
              destructive
            />
            <TitleCardBadgeList
              label="Preferred not selected"
              values={unselectedPreferred}
              muted
            />
          </div>
        </div>
      </div>

      <div className="rounded-md border p-4">
        <h4 className="font-medium">Decision Notes</h4>
        <dl className="mt-3 grid gap-3 text-sm">
          <InlineMeta label="LLM rationale" value={llmRationale} />
          <InlineMeta label="Mismatch reason" value={mismatchReason} />
        </dl>
      </div>

      <details className="rounded-md border p-4 text-sm">
        <summary className="cursor-pointer font-medium">
          Raw diagnostics
        </summary>
        <pre className="mt-3 max-h-[360px] overflow-auto rounded-md bg-muted p-4 text-xs">
          {JSON.stringify(diagnostics, null, 2)}
        </pre>
      </details>
    </div>
  );
};

const TitleCardBadgeList = ({
  label,
  values,
  muted,
  destructive,
}: {
  label: string;
  values: string[];
  muted?: boolean;
  destructive?: boolean;
}) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    {values.length > 0 ? (
      <div className="mt-1 flex flex-wrap gap-2">
        {values.map((value) => (
          <Badge
            key={value}
            variant={
              destructive ? "destructive" : muted ? "outline" : "secondary"
            }
          >
            {value}
          </Badge>
        ))}
      </div>
    ) : (
      <div className="mt-1 text-muted-foreground">None</div>
    )}
  </div>
);

const DecisionBadge = ({
  label,
  value,
}: {
  label: string;
  value: boolean | null;
}) => {
  const variant =
    value === false ? "destructive" : value ? "secondary" : "outline";
  const suffix = value == null ? "Unknown" : value ? "Yes" : "No";

  return <Badge variant={variant}>{`${label}: ${suffix}`}</Badge>;
};

const SceneRow = ({
  scene,
  media,
}: {
  scene: ScriptScene;
  media?: EchoflicksSessionMedia;
}) => {
  const prompt = scene.video_prompt ?? {};
  const titleText = prompt.text ?? prompt.title_card_text;
  const generationPrompt = prompt.prompt ?? prompt.generation_prompt;

  return (
    <div className="grid gap-3 rounded-md border p-3 md:grid-cols-[160px_minmax(0,1fr)]">
      <ScenePreview scene={scene} media={media} />
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge>{scene.id ?? "?"}</Badge>
          <Badge variant="secondary">{scene.type ?? "Scene"}</Badge>
          {scene.type === "StaticImageScene" && isAnimatedScene(scene) ? (
            <Badge variant="outline" className="font-normal">
              animates (secondary i2v)
            </Badge>
          ) : null}
          {durationForScene(scene) ? (
            <Badge variant="outline">
              {formatSeconds(durationForScene(scene))}
            </Badge>
          ) : null}
          {scene.transition ? (
            <Badge variant="outline">
              {scene.transition.replaceAll("_", " ")}
            </Badge>
          ) : null}
        </div>

        <div className="grid gap-2 text-sm sm:grid-cols-2">
          <InlineMeta label="File" value={prompt.file ?? "Title card"} />
          <InlineMeta
            label="Motion"
            value={prompt.static_effect ?? "Generated"}
          />
          <InlineMeta
            label="Card"
            value={prompt.card_type ?? prompt.title_card_bg ?? "None"}
          />
          <InlineMeta
            label="Cuts"
            value={
              Array.isArray(prompt.cuts)
                ? prompt.cuts.join("s - ") + "s"
                : "None"
            }
          />
        </div>

        {titleText ? <p className="font-medium">{titleText}</p> : null}
        {generationPrompt ? (
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">
            {generationPrompt}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            No generation prompt stored for this scene.
          </p>
        )}
      </div>
    </div>
  );
};

const ScenePreview = ({
  scene,
  media,
}: {
  scene: ScriptScene;
  media?: EchoflicksSessionMedia;
}) => {
  const mediaUrl = useDisplayableImage(media);

  if (mediaUrl.url && isImage(media?.mime_type)) {
    return (
      <div className="relative">
        <img
          src={mediaUrl.url}
          alt={media?.s3_key ?? "Scene source"}
          className="aspect-video w-full rounded-md bg-black object-contain"
          loading="lazy"
          onError={mediaUrl.refresh}
        />
        <DisplayCropBadge label="Full frame · contain" isDisplayCrop={false} />
      </div>
    );
  }

  if (mediaUrl.url && isVideo(media?.mime_type)) {
    return (
      <video
        src={mediaUrl.url}
        className="aspect-video w-full rounded-md bg-black object-contain"
        controls
        onError={mediaUrl.refresh}
      />
    );
  }

  if (scene.type === "TitleCardScene") {
    return (
      <div className="flex aspect-video items-center justify-center rounded-md bg-primary p-3 text-center text-sm font-semibold text-primary-foreground">
        {scene.video_prompt?.text ??
          scene.video_prompt?.title_card_text ??
          "Title"}
      </div>
    );
  }

  return (
    <div className="flex aspect-video items-center justify-center rounded-md bg-muted">
      {scene.type === "VideoScene" ? (
        <Video className="size-5 text-muted-foreground" />
      ) : (
        <ImageIcon className="size-5 text-muted-foreground" />
      )}
    </div>
  );
};

const MusicSectionCard = ({ section }: { section: MusicSection }) => (
  <div className="rounded-md border p-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h4 className="font-medium">{section.section_name ?? "Music section"}</h4>
      {section.duration_ms ? (
        <Badge variant="outline">
          {formatSeconds(section.duration_ms / 1000)}
        </Badge>
      ) : null}
    </div>
    <StyleCloud
      title="Positive"
      values={section.positive_local_styles}
      compact
    />
    <StyleCloud
      title="Negative"
      values={section.negative_local_styles}
      muted
      compact
    />
  </div>
);

const AudioDuckingList = ({ ducking }: { ducking: AudioDucking[] }) => {
  if (ducking.length === 0) {
    return <EmptyHint>No audio ducking plan exists in this script.</EmptyHint>;
  }

  return (
    <div className="rounded-md border p-4">
      <h4 className="font-medium">Audio Ducking</h4>
      <div className="mt-3 space-y-2">
        {ducking.map((item, index) => (
          <div
            key={`${item.start_second ?? index}-${item.end_second ?? index}`}
            className="grid grid-cols-[80px_minmax(0,1fr)_80px] items-center gap-3 text-sm"
          >
            <Badge variant="outline">{item.level ?? "level"}</Badge>
            <div className="h-2 rounded-full bg-muted">
              <div
                className="h-2 rounded-full bg-primary"
                style={{ width: duckingWidth(item) }}
              />
            </div>
            <span className="text-right text-muted-foreground">
              {formatTimestamp(item.start_second)} -{" "}
              {formatTimestamp(item.end_second)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const StyleCloud = ({
  title,
  values,
  muted,
  compact,
}: {
  title: string;
  values?: string[];
  muted?: boolean;
  compact?: boolean;
}) => {
  if (!values || values.length === 0) return null;

  return (
    <div className={compact ? "mt-3" : "rounded-md border p-4"}>
      <h4 className="mb-2 text-sm font-medium">{title}</h4>
      <div className="flex flex-wrap gap-2">
        {values.map((value) => (
          <Badge key={value} variant={muted ? "outline" : "secondary"}>
            {value}
          </Badge>
        ))}
      </div>
    </div>
  );
};

const Metric = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) => (
  <div className="rounded-md border p-4">
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      {icon}
      {label}
    </div>
    <div className="mt-2 text-2xl font-semibold">{value}</div>
  </div>
);

const MiniStat = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded-md bg-muted p-3">
    <div className="text-muted-foreground">{label}</div>
    <div className="mt-1 font-semibold">{value}</div>
  </div>
);

const InlineMeta = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => (
  <div className="min-w-0">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="break-words">{value || "None"}</dd>
  </div>
);

const AssetLink = ({ href, label }: { href: string; label: string }) => (
  <Button asChild variant="outline" size="sm">
    <a href={href} target="_blank" rel="noreferrer">
      <FileJson className="size-4" />
      {label}
      <ExternalLink className="size-4" />
    </a>
  </Button>
);

function buildMediaMap(media: EchoflicksSessionMedia[]) {
  const byFilename = new Map<string, EchoflicksSessionMedia>();
  for (const item of media) {
    const filename =
      item.s3_key?.split("/").at(-1) ?? item.local_path?.split(/[\\/]/).at(-1);
    if (filename && !byFilename.has(filename)) {
      byFilename.set(filename, item);
    }
  }
  return byFilename;
}

function sumSceneDuration(scenes: ScriptScene[]) {
  return scenes.reduce((sum, scene) => sum + (durationForScene(scene) ?? 0), 0);
}

function durationForScene(scene: ScriptScene) {
  const prompt = scene.video_prompt;
  if (!prompt) return null;
  if (typeof prompt.length === "number") return prompt.length;
  if (Array.isArray(prompt.cuts) && prompt.cuts.length >= 2) {
    return Math.max(0, Number(prompt.cuts[1]) - Number(prompt.cuts[0]));
  }
  return null;
}

function formatSeconds(seconds?: number | null) {
  if (!seconds) return "0s";
  if (seconds < 60) return `${trimNumber(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

function formatTimestamp(seconds?: number | null) {
  if (seconds == null) return "?:??";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const tenths = Math.round((seconds - Math.floor(seconds)) * 10);
  return tenths > 0
    ? `${mins}:${secs.toString().padStart(2, "0")}.${tenths}`
    : `${mins}:${secs.toString().padStart(2, "0")}`;
}

function trimNumber(value: number) {
  return Number.isInteger(value) ? value.toString() : value.toFixed(1);
}

function voiceLabel(voice?: VoiceGeneration) {
  if (!voice?.gen_type) return "None";
  if (voice.gen_type === "predefined") {
    return voice.voice_id ? `Predefined / ${voice.voice_id}` : "Predefined";
  }
  return voice.prompt ? `Generated / ${voice.prompt}` : "Generated";
}

function jsonSummary(value: unknown) {
  if (value == null) return "None";
  if (Array.isArray(value)) return `${value.length} items`;
  if (typeof value === "object") return `${Object.keys(value).length} fields`;
  return String(value);
}

function getTitleCardDiagnostics(
  diagnostics: ScriptDiagnostics | undefined,
): TitleCardDiagnostics | null {
  if (!isRecord(diagnostics?.title_card)) return null;
  return diagnostics.title_card as TitleCardDiagnostics;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringList(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function stringValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value : null;
}

function booleanValue(value: unknown) {
  return typeof value === "boolean" ? value : null;
}

function duckingWidth(item: AudioDucking) {
  const start = item.start_second ?? 0;
  const end = item.end_second ?? start;
  const width = Math.max(4, Math.min(100, ((end - start) / 60) * 100));
  return `${width}%`;
}

export const scriptFieldSummary = {
  supportedTopLevelFields: [
    "style",
    "script",
    "generated",
    "bboxes",
    "diagnostics",
  ],
  supportedScriptFields: [
    "title",
    "description",
    "video_content",
    "audio_content",
    "music_prompt",
    "voice_generation",
    "aspect_ratio",
    "filter",
    "lut",
    "typography",
  ],
};
